const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function safeUuid(id: string): string {
  if (!UUID_RE.test(id)) throw new Error(`Invalid UUID: ${id}`);
  return id;
}

export const QA_COMPANY_ID = "1188698c-d682-4003-b84c-6330ebf56572";

// QA data lives in the staging environment. From the prod API we reach it via
// a cross-DB connection registered as "staging". When the dashboard itself is
// already talking to the staging API (dev:stage, dashboards.marloo.dev),
// staging IS the default DB, so the override must be dropped.
export const QA_DB: { database?: string } =
  import.meta.env.VITE_ENVIRONMENT === "production"
    ? { database: "staging" }
    : {};

export const DOC_LIST_SQL = `
SELECT
  d.external_id                         AS doc_id,
  d.name,
  d.status::text                        AS status,
  d.template_external_id,
  t.name                                AS template_name,
  length(d.content)::bigint             AS content_len,
  length(COALESCE(d.prompt, ''))::bigint AS prompt_len,
  d.created_date_utc::date              AS created_date
FROM public.document d
LEFT JOIN public.template t ON t.external_id = d.template_external_id
WHERE d.company_external_id = '${QA_COMPANY_ID}'
  AND d.content IS NOT NULL
ORDER BY d.created_date_utc DESC
LIMIT 100
`;

export type DocListRow = {
  doc_id: string;
  name: string;
  status: string;
  template_external_id: string | null;
  template_name: string | null;
  content_len: number;
  prompt_len: number;
  created_date: string;
};

export function buildDocDetailSql(docId: string | null): string {
  if (docId === null) {
    return `SELECT NULL::text AS doc_id, NULL::text AS template_id, NULL::text AS template_name, NULL::text AS template_content, NULL::text AS doc_content, NULL::text AS doc_prompt, NULL::text AS source_name, NULL::text AS source_markdown WHERE FALSE`;
  }
  return `
    WITH src AS (
      SELECT DISTINCT ON (f.template_external_id)
        f.template_external_id, f.name, f.markdown, f.created_date_utc
      FROM public.file f
      WHERE f.file_type = 'template'
        AND f.deleted_date_utc IS NULL
        AND f.markdown IS NOT NULL
      ORDER BY f.template_external_id, f.created_date_utc DESC
    )
    SELECT
      d.external_id::text               AS doc_id,
      t.external_id::text               AS template_id,
      t.name                            AS template_name,
      t.template                        AS template_content,
      d.content                         AS doc_content,
      COALESCE(d.prompt, '')            AS doc_prompt,
      src.name                          AS source_name,
      src.markdown                      AS source_markdown
    FROM public.document d
    LEFT JOIN public.template t ON t.external_id = d.template_external_id
    LEFT JOIN src ON src.template_external_id = t.external_id
    WHERE d.external_id = '${safeUuid(docId)}'
  `;
}

export type DocDetailRow = {
  doc_id: string;
  template_id: string | null;
  template_name: string | null;
  template_content: string | null;
  doc_content: string | null;
  doc_prompt: string | null;
  source_name: string | null;
  source_markdown: string | null;
};

function makeIndexer(columns: string[]) {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  return (row: unknown[], k: string): unknown => {
    const i = idx[k];
    return i === undefined ? undefined : row[i];
  };
}

const str = (v: unknown): string => (v == null ? "" : String(v));
const orNull = (v: unknown): string | null => (v == null ? null : String(v));
const num = (v: unknown): number => (v == null ? 0 : Number(v));

export function mapDocListRows(columns: string[], rows: unknown[][]): DocListRow[] {
  const get = makeIndexer(columns);
  return rows.map((r) => ({
    doc_id:               str(get(r, "doc_id")),
    name:                 str(get(r, "name")),
    status:               str(get(r, "status")),
    template_external_id: orNull(get(r, "template_external_id")),
    template_name:        orNull(get(r, "template_name")),
    content_len:          num(get(r, "content_len")),
    prompt_len:           num(get(r, "prompt_len")),
    created_date:         str(get(r, "created_date")),
  }));
}

export function buildClientFilesSql(docId: string | null): string {
  if (docId === null) {
    return `SELECT NULL::text AS name, NULL::text AS markdown, NULL::text AS raw_text, NULL::text AS file_type WHERE FALSE`;
  }
  return `
    SELECT
      f.name                                AS name,
      f.markdown                            AS markdown,
      f.raw_text                            AS raw_text,
      f.file_type::text                     AS file_type
    FROM public.file f
    WHERE (
      f.client_external_id = (SELECT client_external_id FROM public.document WHERE external_id = '${safeUuid(docId)}')
      OR f.company_external_id = (SELECT company_external_id FROM public.document WHERE external_id = '${safeUuid(docId)}')
         AND f.file_type = 'company_context'
    )
      AND f.file_type IN ('client', 'meeting', 'form', 'company_context')
      AND f.deleted_date_utc IS NULL
      AND (f.markdown IS NOT NULL OR f.raw_text IS NOT NULL)
    ORDER BY f.created_date_utc DESC
    LIMIT 60
  `;
}

export type ClientFileRow = {
  name: string;
  markdown: string | null;
  raw_text: string | null;
  file_type: string | null;
};

export function mapClientFileRows(columns: string[], rows: unknown[][]): ClientFileRow[] {
  const get = makeIndexer(columns);
  return rows.map((r) => ({
    name:      str(get(r, "name")),
    markdown:  orNull(get(r, "markdown")),
    raw_text:  orNull(get(r, "raw_text")),
    file_type: orNull(get(r, "file_type")),
  }));
}

export function mapDocDetailRows(columns: string[], rows: unknown[][]): DocDetailRow[] {
  const get = makeIndexer(columns);
  return rows.map((r) => ({
    doc_id:           str(get(r, "doc_id")),
    template_id:      orNull(get(r, "template_id")),
    template_name:    orNull(get(r, "template_name")),
    template_content: orNull(get(r, "template_content")),
    doc_content:      orNull(get(r, "doc_content")),
    doc_prompt:       orNull(get(r, "doc_prompt")),
    source_name:      orNull(get(r, "source_name")),
    source_markdown:  orNull(get(r, "source_markdown")),
  }));
}
